"use client";
import { Tooltip } from "@mantine/core";
import { ShiftStatusCode } from "../services/shift-service";

export interface ShiftRow {
  id: number;
  name: string;
  code: string;
  start_time: string;
  end_time: string;
  created_at: string;
  statusCode: ShiftStatusCode;
  description?: string;
}

interface ShiftTableProps {
  rows: ShiftRow[];
  isLoading: boolean;
  onEdit?: (row: ShiftRow) => void;
  onDeactivate?: (row: ShiftRow) => void;
  onActivate?: (row: ShiftRow) => void;
}

const ShiftTable: React.FC<ShiftTableProps> = ({
  rows,
  isLoading,
  onEdit,
  onDeactivate,
  onActivate,
}) => {
  if (isLoading) return <div className="p-4 text-center text-gray-500">Đang tải...</div>;
  if (!rows.length) return <div className="p-4 text-center text-gray-500">Không có dữ liệu</div>;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full border-collapse text-sm">
        <thead className="bg-slate-100">
          <tr className="text-left">
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700">STT</th>
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700">Tên ca làm việc</th>
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700">Thời gian bắt đầu</th>
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700">Thời gian kết thúc</th>
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700">Ngày tạo</th>
            <th className="border border-slate-300 px-4 py-3 font-semibold text-slate-700 text-center">Thao tác</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {rows.map((row, idx) => (
            <tr key={row.id} className="hover:bg-slate-50">
              <td className="border border-slate-200 px-4 py-3 text-slate-600">{idx + 1}</td>
              <td className="border border-slate-200 px-4 py-3 font-medium text-slate-800">{row.name}</td>
              <td className="border border-slate-200 px-4 py-3 text-slate-600">{row.start_time}</td>
              <td className="border border-slate-200 px-4 py-3 text-slate-600">{row.end_time}</td>
              <td className="border border-slate-200 px-4 py-3 text-slate-600">{row.created_at}</td>
              <td className="border border-slate-200 px-4 py-3">
                <div className="flex justify-center gap-2">
                  {onEdit && (
                    <Tooltip label="Sửa" withArrow>
                      <button
                        type="button"
                        onClick={() => onEdit(row)}
                        aria-label="Sửa"
                        className="grid h-9 w-9 place-items-center rounded-full bg-yellow-500 text-white shadow-sm transition hover:bg-yellow-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}

                  {row.statusCode === ShiftStatusCode.ACTIVE && onDeactivate && (
                    <Tooltip label="Tạm ngưng" withArrow>
                      <button
                        type="button"
                        onClick={() => onDeactivate(row)}
                        aria-label="Tạm ngưng"
                        className="grid h-9 w-9 place-items-center rounded-full bg-red-500 text-white shadow-sm transition hover:bg-red-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}

                  {row.statusCode === ShiftStatusCode.INACTIVE && onActivate && (
                    <Tooltip label="Kích hoạt" withArrow>
                      <button
                        type="button"
                        onClick={() => onActivate(row)}
                        aria-label="Kích hoạt"
                        className="grid h-9 w-9 place-items-center rounded-full bg-green-500 text-white shadow-sm transition hover:bg-green-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </button>
                    </Tooltip>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShiftTable;